import React, { useState, useEffect } from 'react';
import { Box, VStack, Heading, Text, Button, Spinner, useToast, HStack, Avatar, Center, Image } from '@chakra-ui/react';
import { FaArrowLeft, FaTrophy } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { supabase } from '../services/supabaseClient';
import confetti from 'canvas-confetti';

const MotionBox = motion(Box);

const SorteioScreen = ({ onBack }) => {
  const [mensalistas, setMensalistas] = useState([]);
  const [premios, setPremios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sorteando, setSorteando] = useState(false);
  const [ganhadores, setGanhadores] = useState([]);
  const [premiosSorteados, setPremiosSorteados] = useState([]);
  const toast = useToast();

  // Buscar mensalistas ativos e prêmios
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      
      // Buscar mensalistas
      const { data: mensalistasData, error: mensalistasError } = await supabase
        .from('mensalistas')
        .select('*')
        .eq('status', 'ativo')
        .order('nome');
        
      console.log('Mensalistas encontrados:', mensalistasData?.length || 0);

      if (mensalistasError) {
        console.error('Erro ao buscar mensalistas:', mensalistasError);
        toast({
          title: 'Erro ao carregar mensalistas',
          description: mensalistasError.message,
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
      
      // Buscar prêmios
      const { data: premiosData, error: premiosError } = await supabase
        .from('sorteio_premios')
        .select('*');
      
      console.log('Prêmios encontrados na tabela:', premiosData);
      
      // Usar os prêmios da imagem que o usuário mostrou
      const premiosFixos = [
        { id: 'fa7f16f-a34f-4a59-8772-7f14dce92c6', descricao: 'TESTE1', ordem: 1 },
        { id: '56ea8ebf-ef74-41a5-b57f-c8dad68e0650', descricao: 'TESTE2', ordem: 2 },
        { id: 'bac10dff-924e-4a1c-9691-eb235bb38952', descricao: 'TESTE3', ordem: 3 }
      ];

      if (premiosError || !premiosData || premiosData.length === 0) {
        console.log('Usando prêmios fixos para o sorteio');
        setPremios(premiosFixos);
      } else {
        setPremios(premiosData);
      }

      setMensalistas(mensalistasData || []);
      
      console.log('Total de mensalistas:', mensalistasData?.length || 0);
      console.log('Total de prêmios após ajuste:', premiosFixos.length);
      
      setLoading(false);
    };

    fetchData();
  }, [toast]);

  const realizarSorteio = () => {
    if (mensalistas.length === 0 || premios.length === 0) {
      toast({
        title: 'Sorteio indisponível',
        description: mensalistas.length === 0 ? 'Não há mensalistas ativos para realizar o sorteio.' : 'Não há prêmios cadastrados para o sorteio.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    setSorteando(true);
    setGanhadores([]);
    setPremiosSorteados([]);

    // Lista de mensalistas disponíveis
    let mensalistasDisponiveis = [...mensalistas];
    
    // Ordenar prêmios do maior para o menor (1º, 2º, 3º)
    const premiosOrdenados = [...premios];
    
    // Efeito de sorteio
    let counter = 0;
    const maxIterations = 15; // Número de "giros" antes de parar
    const totalSorteios = Math.min(3, mensalistas.length); // Limitar a 3 sorteios
    let sorteiosRealizados = 0;
    let ultimoSorteado = null;
    
    const interval = setInterval(() => {
      // Sortear aleatório
      const randomMensalistaIndex = Math.floor(Math.random() * mensalistasDisponiveis.length);
      const sorteado = mensalistasDisponiveis[randomMensalistaIndex];
      
      // Atualizar estado temporário para efeito visual
      setGanhadores(prev => {
        const novosGanhadores = [...prev];
        novosGanhadores[sorteiosRealizados] = sorteado;
        return novosGanhadores;
      });
      setPremiosSorteados(prev => {
        const novosPremios = [...prev];
        novosPremios[sorteiosRealizados] = premiosOrdenados[sorteiosRealizados];
        return novosPremios;
      });
      
      counter++;
      if (counter >= maxIterations) {
        // Se o último sorteado for diferente, finalizar o sorteio atual
        if (ultimoSorteado?.id !== sorteado.id) {
          const ganhadorFinal = sorteado;
          const premioFinal = premiosOrdenados[sorteiosRealizados];
          
          // Registrar o sorteio no banco de dados
          registrarSorteio(ganhadorFinal, premioFinal);
          
          // Remover mensalista sorteado da lista
          mensalistasDisponiveis = mensalistasDisponiveis.filter(m => m.id !== ganhadorFinal.id);
          
          // Lançar confetti
          confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
          });
          
          ultimoSorteado = ganhadorFinal;
          sorteiosRealizados++;
          counter = 0;
          
          // Verificar se todos os sorteios foram realizados
          if (sorteiosRealizados >= totalSorteios) {
            clearInterval(interval);
            setSorteando(false);
            
            // Lançar confetti final
            setTimeout(() => {
              confetti({
                particleCount: 200,
                spread: 100,
                origin: { y: 0.6 }
              });
            }, 500);
          }
        } else {
          // Se sorteou o mesmo mensalista, continuar sorteando
          counter = maxIterations - 5;
        }
      }
    }, 100);
  };

  // Função simplificada que não salva no banco de dados
  const registrarSorteio = (ganhador, premio) => {
    console.log(`Sorteio realizado: ${ganhador.nome} ganhou ${premio.descricao}`);
    // Não salva no banco de dados conforme solicitado
  };

  return (
    <Box 
      height="100%" 
      width="100%" 
      px={4} 
      py={6}
      overflowY="auto"
      sx={{
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none'
      }}
    >
      {/* Cabeçalho com logo e botão voltar */}
      <HStack justifyContent="space-between" mb={6}>
        <Button 
          leftIcon={<FaArrowLeft />} 
          onClick={onBack} 
          variant="outline" 
          colorScheme="whiteAlpha"
          size="lg"
        >
          Voltar
        </Button>
        <Image 
          src={require('../assets/Logo-Barber.png')} 
          alt="Logo Barbearia" 
          height="100px" 
          objectFit="contain" 
        />
      </HStack>

      {/* Título da página */}
      <Heading 
        as="h1" 
        size="xl" 
        color="yellow.400" 
        textAlign="center" 
        mb={8}
        fontFamily="heading"
      >
        Sorteio de Mensalistas
      </Heading>

      {/* Conteúdo principal */}
      <VStack spacing={8} align="center" w="100%">
        {loading ? (
          <Center py={20}>
            <Spinner size="xl" color="yellow.400" thickness="4px" />
          </Center>
        ) : (
          <>
            <Text color="gray.300" fontSize="xl" textAlign="center">
              Total de mensalistas participantes: <strong>{mensalistas.length}</strong>
            </Text>
            
            {/* Área do sorteio */}
            <Box 
              w="100%" 
              maxW="600px" 
              bg="gray.900" 
              borderRadius="xl" 
              p={8} 
              boxShadow="dark-lg"
              borderWidth="1px"
              borderColor="gray.700"
            >
              <VStack spacing={8}>
                <Button
                  colorScheme="yellow"
                  size="lg"
                  isLoading={sorteando}
                  loadingText="Sorteando..."
                  onClick={realizarSorteio}
                  py={8}
                  px={12}
                  fontSize="xl"
                  isDisabled={false}
                  w="100%"
                  height="80px"
                  _hover={{ transform: "scale(1.05)" }}
                  transition="all 0.2s"
                  leftIcon={<FaTrophy />}
                >
                  Realizar Sorteio
                </Button>
                
                {ganhadores.length > 0 && (
                  <MotionBox
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    bg="gray.800"
                    borderRadius="lg"
                    p={8}
                    boxShadow="lg"
                    borderWidth="2px"
                    borderColor={sorteando ? "gray.700" : "yellow.500"}
                    width="100%"
                    textAlign="center"
                    animation={!sorteando ? "pulse 2s infinite" : undefined}
                    sx={{
                      "@keyframes pulse": {
                        "0%": { boxShadow: "0 0 0 0 rgba(236, 201, 75, 0.7)" },
                        "70%": { boxShadow: "0 0 0 15px rgba(236, 201, 75, 0)" },
                        "100%": { boxShadow: "0 0 0 0 rgba(236, 201, 75, 0)" }
                      }
                    }}
                  >
                    <VStack spacing={4}>
                      <Heading size="md" color="yellow.400" mb={6}>
                        Resultado do Sorteio
                      </Heading>

                      <VStack spacing={6} width="100%">
                        {ganhadores.map((ganhador, index) => {
                          const premio = premiosSorteados[index];
                          if (!ganhador || !premio) return null;
                          
                          // Mapeia o índice para a posição correta (0 -> 1º, 1 -> 2º, 2 -> 3º)
                          const posicoes = [1, 2, 3];
                          const posicao = posicoes[index] || 3; // Fallback para 3º lugar se o índice for inválido
                          
                          const cores = {
                            1: { bg: "yellow.400", text: "black", border: "yellow.500", label: "1º Lugar - Ouro" },
                            2: { bg: "gray.300", text: "black", border: "gray.400", label: "2º Lugar - Prata" },
                            3: { bg: "#CD7F32", text: "black", border: "#A66529", label: "3º Lugar - Bronze" }
                          };
                          
                          const cor = cores[posicao] || cores[3]; // Fallback para bronze se a posição for inválida
                          
                          return (
                            <Box
                              key={ganhador.id}
                              bg="gray.800"
                              p={4}
                              borderRadius="lg"
                              width="100%"
                              borderWidth="2px"
                              borderColor={cor.border}
                              position="relative"
                              _before={{
                                content: `"${cor.label}"`,
                                position: "absolute",
                                top: "-12px",
                                left: "50%",
                                transform: "translateX(-50%)",
                                bg: cor.bg,
                                color: cor.text,
                                px: 4,
                                py: 1,
                                borderRadius: "full",
                                fontSize: "sm",
                                fontWeight: "bold"
                              }}
                            >
                              <VStack spacing={4}>
                                <HStack spacing={4} width="100%">
                                  <Avatar 
                                    size="lg" 
                                    name={ganhador.nome} 
                                    src={ganhador.foto_url} 
                                    bg={cor.bg}
                                    border="3px solid"
                                    borderColor={cor.border}
                                  />
                                  <VStack align="start" flex={1}>
                                    <Heading size="md" color="white">
                                      {ganhador.nome}
                                    </Heading>
                                    <Text color="gray.300" fontSize="sm">
                                      {ganhador.telefone || "Sem telefone cadastrado"}
                                    </Text>
                                  </VStack>
                                </HStack>
                                
                                <Box
                                  bg={cor.bg}
                                  color={cor.text}
                                  px={4}
                                  py={2}
                                  borderRadius="md"
                                  fontWeight="bold"
                                  width="100%"
                                  textAlign="center"
                                >
                                  {premio.descricao}
                                </Box>
                              </VStack>
                            </Box>
                          );
                        })}
                      </VStack>
                    </VStack>
                  </MotionBox>
                )}
              </VStack>
            </Box>
            
            {/* Informações do sorteio */}
            {(mensalistas.length > 0 || premios.length > 0) && (
              <Box 
                w="100%" 
                maxW="600px" 
                bg="gray.900" 
                borderRadius="xl" 
                p={6} 
                mt={6}
                borderWidth="1px"
                borderColor="gray.700"
              >
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Heading size="md" color="gray.300" mb={2}>
                      Prêmios Disponíveis
                    </Heading>
                    <VStack spacing={2} align="stretch">
                      {premios.map((premio, index) => (
                        <HStack key={premio.id} bg="gray.800" p={3} borderRadius="md">
                          <Text color="yellow.400" fontWeight="bold">{index + 1}º</Text>
                          <Text color="white">{premio.descricao}</Text>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>

                  <Box
                    width="100%"
                    mt={4}
                  >
                    <Heading size="md" color="gray.300" mb={2}>
                      Participantes ({mensalistas.length})
                    </Heading>
                    <VStack 
                      spacing={3} 
                      align="stretch" 
                      maxH="350px"
                      overflowY="auto"
                      css={{
                        "&::-webkit-scrollbar": {
                          width: "8px",
                          borderRadius: "8px",
                          backgroundColor: "#2D3748"
                        },
                        "&::-webkit-scrollbar-thumb": {
                          backgroundColor: "#4A5568",
                          borderRadius: "8px"
                        },
                        scrollbarWidth: "thin",
                        scrollbarColor: "#4A5568 #2D3748"
                      }}
                    >
                      {mensalistas.map((mensalista) => (
                        <HStack 
                          key={mensalista.id} 
                          bg="gray.800" 
                          p={3} 
                          borderRadius="md"
                          spacing={4}
                        >
                          <Avatar 
                            size="sm" 
                            name={mensalista.nome} 
                            src={mensalista.foto_url} 
                          />
                          <Text color="white">
                            {mensalista.nome}
                          </Text>
                        </HStack>
                      ))}
                    </VStack>
                  </Box>
                </VStack>
              </Box>
            )}
          </>
        )}
      </VStack>
    </Box>
  );
};

export default SorteioScreen;
